import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { PublishPayload } from '@utils/types/publishing/publish.ts';

const ACTIVE_PILL_TEXT_LIGHT = 'firstLayer';
const INACTIVE_PILL_TEXT = 'normalText';

interface Props {
  value: PublishPayload;
  onChange: (payload: PublishPayload) => void;
  /** ISO language codes the document actually has captions for. */
  availableLanguages: string[];
  /** Optional saved transcript templates for the transcript branch. */
  transcriptTemplates?: { id: string; label: string }[];
  /** Supported transcript formats shown in the format picker. */
  transcriptFormats: { id: string; label: string }[];
  /** If both kinds are available, let the user toggle between them. */
  allowKindToggle: boolean;
}

/**
 * What exactly do we send to the platform. Captions path shows a
 * multi-select of ISO codes; transcript path shows a format +
 * optional template.
 */
export default function StepChoosePayload({
  value,
  onChange,
  availableLanguages,
  transcriptTemplates,
  transcriptFormats,
  allowKindToggle
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const kind = value.kind;

  const switchKind = (next: 'captions' | 'transcript') => {
    if (next === kind) return;
    onChange(
      next === 'captions'
        ? {
            kind: 'captions',
            languages:
              value.kind === 'captions'
                ? value.languages
                : availableLanguages.slice(0, 2)
          }
        : {
            kind: 'transcript',
            format:
              value.kind === 'transcript'
                ? value.format
                : (transcriptFormats[0]?.id ?? 'docx')
          }
    );
  };

  const toggleLanguage = (code: string) => {
    if (value.kind !== 'captions') return;
    const has = value.languages.includes(code);
    const next = has
      ? value.languages.filter(c => c !== code)
      : [...value.languages, code];
    onChange({ kind: 'captions', languages: next });
  };

  return (
    <div className="flex flex-col gap-4">
      {allowKindToggle && (
        <div
          role="radiogroup"
          aria-label={t('publish-payload-kind-label')}
          className="flex gap-2"
        >
          {(['captions', 'transcript'] as const).map(k => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => switchKind(k)}
              className="rounded-full border px-3 py-1 text-sm"
              style={{
                borderColor:
                  kind === k ? getColour('activeText') : getColour('hairline'),
                background:
                  kind === k ? getColour('activeText') : 'transparent',
                color: getColour(
                  kind === k ? ACTIVE_PILL_TEXT_LIGHT : INACTIVE_PILL_TEXT
                )
              }}
            >
              {t(
                k === 'captions'
                  ? 'publish-payload-kind-captions'
                  : 'publish-payload-kind-transcript'
              )}
            </button>
          ))}
        </div>
      )}

      {kind === 'captions' && (
        <fieldset
          className="flex flex-col gap-2"
          aria-labelledby="publish-captions-legend"
        >
          <legend
            id="publish-captions-legend"
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('publish-payload-captions-languages')}
          </legend>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('publish-payload-captions-help')}
          </p>
          {availableLanguages.length === 0 ? (
            <div
              className="rounded-lg border p-3 text-sm"
              style={{
                borderColor: getColour('hairline'),
                background: getColour('secondLayer'),
                color: getColour('normalText')
              }}
            >
              <p>{t('publish-payload-captions-empty')}</p>
              {allowKindToggle && (
                <button
                  type="button"
                  onClick={() => switchKind('transcript')}
                  className="mt-2 text-sm font-medium underline"
                  style={{ color: getColour('activeText') }}
                >
                  {t('publish-payload-captions-empty-switch')}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableLanguages.map(code => {
                const picked = value.languages.includes(code);
                const inputId = `publish-caption-lang-${code}`;
                return (
                  <label
                    key={code}
                    htmlFor={inputId}
                    className="flex items-center gap-2 rounded-full border px-3 py-1 text-sm outline-none focus-within:ring-2"
                    style={{
                      borderColor: picked
                        ? getColour('activeText')
                        : getColour('hairline'),
                      background: picked
                        ? getColour('secondLayer')
                        : 'transparent'
                    }}
                  >
                    <input
                      id={inputId}
                      type="checkbox"
                      className="sr-only"
                      checked={picked}
                      onChange={() => toggleLanguage(code)}
                    />
                    <span>{code.toUpperCase()}</span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>
      )}

      {kind === 'transcript' && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="publish-transcript-format"
              className="text-sm font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('publish-payload-transcript-format')}
            </label>
            <select
              id="publish-transcript-format"
              value={value.format}
              onChange={e =>
                onChange({
                  ...value,
                  format: e.target.value
                })
              }
              className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
              style={{
                borderColor: getColour('inputBorder'),
                background: getColour('firstLayer'),
                color: getColour('normalText')
              }}
            >
              {transcriptFormats.map(f => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          {transcriptTemplates && transcriptTemplates.length > 0 && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="publish-transcript-template"
                className="text-sm font-medium"
                style={{ color: getColour('normalText') }}
              >
                {t('publish-payload-transcript-template')}
              </label>
              <select
                id="publish-transcript-template"
                value={value.template_id ?? ''}
                onChange={e =>
                  onChange({
                    ...value,
                    template_id: e.target.value || undefined
                  })
                }
                className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
                style={{
                  borderColor: getColour('inputBorder'),
                  background: getColour('firstLayer'),
                  color: getColour('normalText')
                }}
              >
                <option value="">
                  {t('publish-payload-transcript-template-none')}
                </option>
                {transcriptTemplates.map(tpl => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
